import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from '../AdminOnboardingUsersTable.module.css';
import loadingStyles from '../shared/Loading.module.css';
import BackButton from '../shared/BackButton';
import dashStyles from '../Dashboard.module.css';
import profileStyles from '../UserProfile.module.css';
import BrandLogo from '../BrandLogo';
import logoImage from '../../assets/logo.png';
import { FINANCIAL_PROFILE_CURRENCY_INPUT_IDS } from '../OnboardingAgent/FinancialProfilePanel';
import { formatResponse, decodeHtmlEntities } from '../../utils/helper';
import { getSectionIcon } from '../UserProfile/sectionIcons';
import { getUserAnswer as getDetailUserAnswer } from './renderAnswer';
import { createInitialSectionState, categoryToSectionKey } from './utils';
import { normalizeOnboardingProgressKeys } from '../../utils/onboardingUtils';
import FinancialDashboard from '../../utils/FinancialDashboard';
import {
  AdminTableLoadingState,
  getAdminReadOnlyAnswerDisplay,
} from './helpers';

function portalToBody(node) {
  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
}

function adminReadOnlyInputClassName(question, isCurrencyNumber) {
  const base = styles.readOnlyInput;
  if (question?.type === 'number') {
    if (isCurrencyNumber) {
      return `${base} ${profileStyles['number-input']} ${profileStyles['currency-number-input']}`;
    }
    return `${base} ${profileStyles['number-input']}`;
  }
  return `${base} ${profileStyles['profile-text-input']}`;
}

function FullScreenModal({
  onClose,
  titleId,
  title,
  subtitle,
  contentClassName,
  bodyClassName,
  children,
}) {
  return portalToBody(
    <div className={styles['modal-overlay']} onClick={onClose} role="presentation">
      <div
        className={`${styles['modal-content']} ${styles['detailModal']} ${contentClassName}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles['detailProfileShell']}>
          <header className={dashStyles.headerBar}>
            <div className={dashStyles.headerLeft}>
              <BackButton className={dashStyles.headerBackButton} onClick={onClose}>
                Close
              </BackButton>
              <div className={dashStyles.headerTitleGroup}>
                <h1 id={titleId} className={dashStyles.headerTitle}>{title}</h1>
                <p className={dashStyles.headerSubtitle}>{subtitle}</p>
              </div>
            </div>
          </header>
          <div className={`${styles['detailMainContent']} ${bodyClassName}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export function UserDetailModal({
  user,
  loading,
  structuredCategories,
  viewContext,
  onClose,
}) {
  const selectedUserKey = user?.username || user?.id || '';
  const [expandedSections, setExpandedSections] = useState(() =>
    createInitialSectionState(structuredCategories, false)
  );

  const lastCategoriesSignatureRef = useRef('');
  const lastUserRef = useRef('');

  useEffect(() => {
    const signature = structuredCategories.map((category) => categoryToSectionKey(category)).join('|');
    if (
      lastCategoriesSignatureRef.current === signature &&
      lastUserRef.current === selectedUserKey
    ) {
      return;
    }

    lastCategoriesSignatureRef.current = signature;
    lastUserRef.current = selectedUserKey;

    setExpandedSections((prev) => {
      const nextState = createInitialSectionState(structuredCategories, false);
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(nextState);
      const sameStructure =
        prevKeys.length === nextKeys.length && nextKeys.every((key) => prevKeys.includes(key));
      return sameStructure ? prev : nextState;
    });
  }, [structuredCategories, selectedUserKey]);

  const handleSectionToggle = useCallback((sectionKey) => {
    setExpandedSections((prev) => {
      const isCurrentlyOpen = !!prev[sectionKey];
      const nextState = {};
      structuredCategories.forEach((category) => {
        const key = categoryToSectionKey(category);
        nextState[key] = key === sectionKey ? !isCurrentlyOpen : false;
      });
      if (!Object.prototype.hasOwnProperty.call(nextState, sectionKey)) {
        nextState[sectionKey] = !isCurrentlyOpen;
      }
      return nextState;
    });
  }, [structuredCategories]);

  const renderReadOnlyChatLayout = (titleMarkup, answerNode) => (
    <div className={`${profileStyles['answer-section']} ${styles['detailAnswerSection']}`}>
      <div className={profileStyles['ai-chat-bubble']}>
        <BrandLogo
          src={logoImage}
          alt="Richuel logo"
          className={profileStyles['ai-avatar']}
          decoding="async"
        />
        <div className={profileStyles['ai-message']}>
          <div className={`${profileStyles['ai-name']} ${styles['detailAiName']}`}>Reese AI</div>
          <div className={`${profileStyles['ai-question']} ${styles['detailAiQuestion']}`}>
            <span dangerouslySetInnerHTML={{ __html: titleMarkup }} />
          </div>
        </div>
      </div>

      <div className={`${profileStyles['content-avatar-layout']} ${styles['detailContentLayout']}`}>
        <div className={`${profileStyles['content-section']} ${styles['detailContentSection']}`}>
          <div className={styles['readonly-answer-card']}>
            <div className={styles['readonly-answer-content']}>{answerNode}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <FullScreenModal
      onClose={onClose}
      titleId="admin-detail-modal-title"
      title="Onboarding Details"
      subtitle={`Review responses for ${user.name}`}
      contentClassName={styles['userDetailModal']}
      bodyClassName={styles['userDetailContent']}
    >
      {loading ? (
        <AdminTableLoadingState message="Loading onboarding data..." />
      ) : structuredCategories.length === 0 ? (
        <div className={profileStyles['no-data']}>
          <p>No onboarding questions available for this user.</p>
        </div>
      ) : (
        <div className={`${profileStyles['profile-sections']} ${styles['detailProfileSections']}`}>
          {structuredCategories.map((category, categoryIndex) => {
            const sectionKey = categoryToSectionKey(category);
            const isExpanded = !!expandedSections[sectionKey];
            const questionsForCategory = category.questions || [];

            return (
              <div key={sectionKey || categoryIndex} className={`${profileStyles['profile-section']} ${styles['detailProfileSection']}`}>
                <div
                  className={`${profileStyles['section-header']} ${styles['detailSectionHeader']} ${isExpanded ? profileStyles['expanded'] : ''}`}
                  onClick={() => handleSectionToggle(sectionKey)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleSectionToggle(sectionKey);
                    }
                  }}
                >
                  <div className={`${profileStyles['section-icon']} ${styles['detailSectionIcon']}`}>{getSectionIcon(category.name, category.id)}</div>
                  <div className={`${profileStyles['section-title']} ${styles['detailSectionTitle']}`}>{category.name}</div>
                  <div className={`${profileStyles['section-chevron']} ${styles['detailSectionChevron']} ${isExpanded ? profileStyles['expanded'] : ''}`}>
                    ▼
                  </div>
                </div>

                {isExpanded ? (
                  <div className={`${profileStyles['section-content']} ${styles['detailSectionContent']}`}>
                    {questionsForCategory.length === 0 ? (
                      <div className={styles['no-question-placeholder']}>
                        No questions in this category yet.
                      </div>
                    ) : (
                      questionsForCategory.map((question, questionIndex) => {
                        const rawAnswer = getDetailUserAnswer(
                          question,
                          viewContext.mergedOnboarding,
                          category.id
                        );
                        const answer =
                          rawAnswer === null || rawAnswer === undefined
                            ? ''
                            : Array.isArray(rawAnswer)
                              ? JSON.stringify(rawAnswer)
                              : typeof rawAnswer === 'object'
                                ? JSON.stringify(rawAnswer)
                                : String(rawAnswer);

                        const questionTitle =
                          viewContext.questionsMap[question.id] || question.title || question.id;
                        const titleMarkup = question?.id === 'children_college_years'
                          ? decodeHtmlEntities(questionTitle)
                          : formatResponse(questionTitle);
                        const isCurrencyNumber =
                          question.type === 'number' && FINANCIAL_PROFILE_CURRENCY_INPUT_IDS.has(question.id);
                        const displayValue = getAdminReadOnlyAnswerDisplay(question, answer || '');
                        const inputEl = (
                          <input
                            type="text"
                            value={displayValue}
                            readOnly
                            className={adminReadOnlyInputClassName(question, isCurrencyNumber)}
                            placeholder="No answer provided"
                            autoComplete="off"
                          />
                        );
                        const answerNode =
                          question.type === 'number' ? (
                            <div className={profileStyles['number-input-container']}>{inputEl}</div>
                          ) : (
                            inputEl
                          );

                        return (
                          <div
                            key={question.id || questionIndex}
                            className={profileStyles['question-item']}
                          >
                            {renderReadOnlyChatLayout(titleMarkup, answerNode)}
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </FullScreenModal>
  );
}

export function DashboardDataModal({
  user,
  loading,
  onboardingProgress,
  checkupAndActionPlan,
  onDisplayMetricsReady,
  onClose,
}) {
  return (
    <FullScreenModal
      onClose={onClose}
      titleId="admin-dashboard-data-modal-title"
      title="Dashboard Data"
      subtitle={`Insights for ${user.name}`}
      contentClassName={styles['dashboardModal']}
      bodyClassName={styles['dashboardDetailContent']}
    >
      {loading ? (
        <AdminTableLoadingState message="Loading dashboard data..." />
      ) : (
        <FinancialDashboard
          onboardingProgress={onboardingProgress || normalizeOnboardingProgressKeys(user.onboarding || {})}
          onboardingAiCheckupAndActionPlan={checkupAndActionPlan}
          isModal
          hideActionCardPills
          showHeader={false}
          isAdmin
          useAdminEndpointForSummary
          summaryUsername={user.username}
          displayUserName={user.name}
          onDisplayMetricsReady={onDisplayMetricsReady}
        />
      )}
    </FullScreenModal>
  );
}

export function ChartDetailModal({ user, html, onClose }) {
  return (
    <FullScreenModal
      onClose={onClose}
      titleId="admin-chart-detail-modal-title"
      title="Chart Detail"
      subtitle={`Insights for ${user.name}`}
      contentClassName={styles['chartDetailModal']}
      bodyClassName={styles['chartDetailContent']}
    >
      {html ? (
        <div className={styles['chart-detail-wrapper']}>
          <iframe
            srcDoc={html}
            title="Chart Report"
            className={styles['chart-detail-iframe']}
            sandbox="allow-scripts"
          />
        </div>
      ) : (
        <div className={`${loadingStyles['loading-container']} ${styles['chart-detail-empty']}`}>
          <p>No chart data available.</p>
        </div>
      )}
    </FullScreenModal>
  );
}

export function DeleteUserConfirmModal({
  user,
  submitting,
  error,
  onConfirm,
  onClose,
}) {
  const displayName = String(user.name ?? '').trim() || 'this user';

  return (
    <div
      className={`${styles['modal-overlay']} ${styles['deleteConfirmOverlay']}`}
      onClick={() => {
        if (!submitting) onClose();
      }}
      role="presentation"
    >
      <div
        className={styles['deleteConfirmCard']}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="admin-delete-user-title"
        aria-describedby="admin-delete-user-desc"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles['deleteConfirmHeader']}>
          <h2 id="admin-delete-user-title" className={styles['deleteConfirmTitle']}>
            Delete this user?
          </h2>
          <button
            type="button"
            className={styles['deleteConfirmClose']}
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles['deleteConfirmBody']}>
          <p id="admin-delete-user-desc" className={styles['deleteConfirmLead']}>
            You are about to permanently remove <strong>{displayName}</strong> from Richuel.
          </p>

          <div className={styles['deleteConfirmWarning']} role="note">
            <p className={styles['deleteConfirmWarningTitle']}>This will:</p>
            <ul className={styles['deleteConfirmList']}>
              <li>Remove the user account from the database</li>
            </ul>
            <p className={styles['deleteConfirmWarningFoot']}>This action cannot be undone.</p>
          </div>

          {error ? (
            <div className={styles['deleteConfirmError']} role="alert">
              {error}
            </div>
          ) : null}
        </div>

        <div className={styles['deleteConfirmActions']}>
          <button
            type="button"
            className={styles['deleteConfirmBtnCancel']}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles['deleteConfirmBtnDanger']}
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className={styles['deleteConfirmBtnSpinner']} aria-hidden />
                Deleting…
              </>
            ) : (
              'Delete User'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
